import { HTTPError } from "ky-universal";
import api, {
  addAccessToken,
  addPrefixUrl,
  extendAPI,
} from "@gocardless/api/utils/api";
import {
  ErrorInterface,
  getErrorJSONFromErrorResponse,
} from "@gocardless/api/utils";
import { HTTPStatusCode } from "@gocardless/api/http/http-status-codes";
import {
  Endpoint,
  getEndpointURL,
} from "@gocardless/api/dashboard/common/endpoints";

import { EventBus } from "./bus";
import { Locale } from "./i18n";
import { Environment, EnvironmentApiUrl } from "./types/environments";
import { GoCardlessError } from "./types";
import { captureSentryError } from "./helpers/sentry";

export enum ErrorReason {
  ACCESS_TOKEN_NOT_ACTIVE = "access_token_not_active",
}

export const createApiClient = (
  environment: Environment,
  sessionToken: () => Promise<string>,
  bus: EventBus,
  locale: Locale
) => {
  addPrefixUrl(EnvironmentApiUrl[environment]);

  // Remember Me requests (both the "is this customer recognised" self check
  // and the "save these details" create) hit this same path regardless of
  // which web component (billing-request, remember-me-signup,
  // remember-me-return-flow) happens to be mounted, or whether the call goes
  // through the generated SDK hooks directly or via this client's own
  // request/register wrapper. We tag them here so `component-type` reliably
  // identifies the Remember Me feature for tracking, matching the old
  // ComponentType.REMEMBER_ME behaviour.
  const rememberMeRequestPath = getEndpointURL(
    Endpoint.RememberCustomerDetailSelf
  );

  extendAPI({
    hooks: {
      beforeRequest: [
        (request) => {
          request.headers.set("source", "gc-components");
          request.headers.set("component-version", __PACKAGE_VERSION__);
          request.headers.set("Accept-Language", locale);

          if (request.url.includes(rememberMeRequestPath)) {
            request.headers.set("component-type", "remember_me");
          }
        },
      ],
    },
  });

  let tokenPromise: Promise<string> | null = null;

  const mintToken = (): Promise<string> => {
    const promise = sessionToken()
      .then((token) => {
        addAccessToken(token);
        return token;
      })
      .catch((error) => {
        tokenPromise = null;
        captureSentryError(error);
        throw error;
      });
    tokenPromise = promise;
    return promise;
  };

  const request = async <T>(
    componentType: string,
    path: string,
    options: {
      method: "get" | "post";
      json?: unknown;
      headers?: Record<string, string>;
    },
    isRetry = false
  ): Promise<T> => {
    bus.emit("api:requestStart", { path });

    const send = () =>
      api.API[options.method](path, {
        json: options.json,
        headers: { ...options.headers, "component-type": componentType },
      });

    try {
      await (tokenPromise ?? mintToken());

      const response = await send();

      bus.emit("api:requestEnd", { path, status: response.status });
      return (await response.json()) as T;
    } catch (error) {
      const gcError = await toGoCardlessError(error);
      const isSessionTokenExpired = !gcError.isUnrecoverable;

      if (isSessionTokenExpired && !isRetry) {
        await mintToken();
        return request(componentType, path, options, true);
      }

      const finalError = isSessionTokenExpired
        ? { ...gcError, isUnrecoverable: true }
        : gcError;

      bus.emit("api:error", { path, error: finalError });
      captureSentryError(finalError);
      throw finalError;
    }
  };

  return {
    register: (componentType: string) => ({
      get: <T>(path: string, headers?: Record<string, string>) =>
        request<T>(componentType, path, { method: "get", headers }),
      post: <T>(
        path: string,
        json?: unknown,
        headers?: Record<string, string>
      ) => request<T>(componentType, path, { method: "post", json, headers }),
    }),
  };
};

const isHttpError = (error: unknown): error is HTTPError =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  (error as { response?: unknown }).response instanceof Response;

const toGoCardlessError = async (error: unknown): Promise<GoCardlessError> => {
  if (!isHttpError(error)) {
    return {
      code: 500,
      message: (error as Error)?.message ?? "Network error",
      isUnrecoverable: true,
    };
  }

  try {
    const response = await getErrorJSONFromErrorResponse(error);

    if (!response) {
      return {
        code: error.response.status,
        message: error.message,
        isUnrecoverable: true,
      };
    }

    const statusCode = response.code;

    if (statusCode === HTTPStatusCode.Unauthorized) {
      const sessionTokenExpired = hasErrorResponseWithReason(
        response,
        ErrorReason.ACCESS_TOKEN_NOT_ACTIVE
      );

      if (sessionTokenExpired) {
        return {
          code: response?.code ?? error.response.status,
          message: response?.message ?? error.message,
          isUnrecoverable: false,
          type: response.type,
          requestId: response.request_id,
          errors: response.errors,
        };
      }
    }

    return {
      code: response?.code ?? error.response.status,
      message: response?.message ?? error.message,
      isUnrecoverable: true,
      type: response.type,
      requestId: response.request_id,
      errors: response.errors,
    };
  } catch {
    return {
      code: error.response.status,
      message: error.message,
      isUnrecoverable: true,
    };
  }
};

const hasErrorResponseWithReason = (
  errorResponse: ErrorInterface,
  reason: string
) => {
  return !!errorResponse.errors?.find((e) => e.reason === reason);
};

export type ApiClient = ReturnType<
  ReturnType<typeof createApiClient>["register"]
>;
